import { useState, type FormEvent } from 'react'
import {
  CAlert,
  CButton,
  CForm,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { consoleApi, apiErrorMessage } from '../../api/client'
import type { WorkloadRecord } from '../../api/types'
import { EmptyState, ErrorState, LoadingState } from '../../components/common/AsyncState'
import { PageHeader } from '../../components/common/PageHeader'
import { formatDateTime } from '../../utils/format'

const local = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
const initial = () => ({
  title: '',
  projectAlias: '',
  publicSummary: '',
  startsAt: local(new Date(Date.now() + 3_600_000)),
  endsAt: local(new Date(Date.now() + 9 * 3_600_000)),
  status: 'planned',
  visibility: 'internal',
  gpuCount: 1,
})

export function WorkloadsPage() {
  const client = useQueryClient()
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(initial)
  const query = useQuery({ queryKey: ['console', 'workloads'], queryFn: consoleApi.workloads })
  const refresh = () => client.invalidateQueries({ queryKey: ['console', 'workloads'] })
  const create = useMutation({
    mutationFn: consoleApi.createWorkload,
    onSuccess: async () => {
      setModal(false)
      setForm(initial())
      await refresh()
    },
  })
  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: WorkloadRecord['status'] }) =>
      consoleApi.updateWorkloadStatus(id, status),
    onSuccess: refresh,
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate({
      ...form,
      startsAt: new Date(form.startsAt).toISOString(),
      endsAt: new Date(form.endsAt).toISOString(),
      gpuCount: Number(form.gpuCount),
      ...(form.publicSummary ? { publicSummary: form.publicSummary } : {}),
    })
  }
  return (
    <>
      <PageHeader
        eyebrow="Allocation and operating context"
        title="Workloads & reservations"
        description="Schedule research work and provide a public-safe context that can be reflected in the Observatory without revealing private job details."
        actions={
          <CButton color="primary" onClick={() => setModal(true)}>
            Schedule workload
          </CButton>
        }
      />
      {(create.error || update.error) && (
        <CAlert color="danger">{apiErrorMessage(create.error ?? update.error)}</CAlert>
      )}
      {query.isLoading && <LoadingState />}
      {query.isError && <ErrorState message={apiErrorMessage(query.error)} />}
      {query.data &&
        (query.data.length ? (
          <div className="workload-table-wrap">
            <table className="workload-table">
              <thead>
                <tr>
                  <th>Workload</th>
                  <th>Project</th>
                  <th>Window (ICT)</th>
                  <th>Visibility</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <strong>{item.title}</strong>
                      <small>{item.publicSummary ?? 'Internal context only'}</small>
                    </td>
                    <td>
                      <code>{item.projectAlias}</code>
                    </td>
                    <td>
                      {formatDateTime(item.startsAt)}
                      <small>to {formatDateTime(item.endsAt)}</small>
                    </td>
                    <td>{item.visibility}</td>
                    <td>
                      <CFormSelect
                        aria-label={`Status for ${item.title}`}
                        value={item.status}
                        disabled={update.isPending}
                        onChange={(event) =>
                          update.mutate({
                            id: item._id,
                            status: event.target.value as WorkloadRecord['status'],
                          })
                        }
                      >
                        <option value="planned">Planned</option>
                        <option value="queued">Queued</option>
                        <option value="running">Running</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </CFormSelect>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No workloads are visible to your account"
            description="Schedule the first workload or ask an operator to review ownership."
          />
        ))}
      <CModal visible={modal} onClose={() => setModal(false)} size="lg">
        <CModalHeader>
          <CModalTitle>Schedule workload</CModalTitle>
        </CModalHeader>
        <CForm onSubmit={submit}>
          <CModalBody>
            {create.error && <CAlert color="danger">{apiErrorMessage(create.error)}</CAlert>}
            <div className="form-grid">
              <label className="form-grid__full">
                Internal workload title
                <CFormInput
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  required
                />
              </label>
              <label>
                Project alias
                <CFormInput
                  value={form.projectAlias}
                  onChange={(event) => setForm({ ...form, projectAlias: event.target.value })}
                  placeholder="LR-024"
                  required
                />
              </label>
              <label>
                GPU count
                <CFormInput
                  type="number"
                  min={1}
                  max={32}
                  value={form.gpuCount}
                  onChange={(event) => setForm({ ...form, gpuCount: Number(event.target.value) })}
                />
              </label>
              <label>
                Starts at
                <CFormInput
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
                  required
                />
              </label>
              <label>
                Ends at
                <CFormInput
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
                  required
                />
              </label>
              <label>
                Status
                <CFormSelect
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  <option value="planned">Planned</option>
                  <option value="queued">Queued</option>
                  <option value="running">Running</option>
                </CFormSelect>
              </label>
              <label>
                Visibility
                <CFormSelect
                  value={form.visibility}
                  onChange={(event) => setForm({ ...form, visibility: event.target.value })}
                >
                  <option value="internal">Internal only</option>
                  <option value="public">Public schedule</option>
                  <option value="embargoed">Embargoed</option>
                </CFormSelect>
              </label>
              <label className="form-grid__full">
                Public-safe summary (optional)
                <CFormTextarea
                  rows={3}
                  value={form.publicSummary}
                  onChange={(event) => setForm({ ...form, publicSummary: event.target.value })}
                  maxLength={600}
                />
              </label>
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="light" onClick={() => setModal(false)}>
              Cancel
            </CButton>
            <CButton color="primary" type="submit" disabled={create.isPending}>
              Save workload
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  )
}
